/**
 * Subscription tier definitions and free tier limits
 * Central config for the JETC freemium model
 */

export type SubscriptionPlan = 'free' | 'premium';
export type SubscriptionStatus = 'active' | 'inactive' | 'past_due' | 'canceled';

export interface SubscriptionInfo {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  stripePriceId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

/** Free tier limits */
export const FREE_TIER_LIMITS = {
  /** Max AI chat conversations per day */
  dailyChatConversations: 5,
  /** Max saved verses */
  maxSavedVerses: 20,
  /** Allowed journal types on free tier */
  journalTypes: ['freeform'] as const,
  /** Allowed chat modes on free tier */
  chatModes: ['study'] as const,
} as const;

/** All journal types available on premium */
export const ALL_JOURNAL_TYPES = ['freeform', 'soap', 'prayer', 'gratitude'] as const;

/** All chat modes available on premium */
export const ALL_CHAT_MODES = ['study', 'prayer', 'devotional', 'topic', 'life'] as const;

/** Stripe price IDs — set these after creating products in Stripe Dashboard */
export const STRIPE_PRICES = {
  monthly: import.meta.env.VITE_STRIPE_PRICE_MONTHLY || '',
  annual: import.meta.env.VITE_STRIPE_PRICE_ANNUAL || '',
} as const;

/** Pricing display values */
export const PRICING = {
  monthly: 4.99,
  annual: 39.99,
  annualMonthly: 3.33, // $39.99 / 12
} as const;

/** Feature comparison for paywall / pricing page */
export const FEATURE_COMPARISON = {
  free: [
    '5 AI chat conversations per day',
    'Full Bible reader (offline)',
    'Freeform journal',
    'Daily check-in',
    'Save up to 20 verses',
  ],
  premium: [
    'Unlimited AI conversations',
    'All 5 conversation modes',
    'All 4 journal types (SOAP, Prayer, Gratitude, Freeform)',
    'Unlimited verse collections',
    'Growth dashboard with full analytics',
    'Priority AI response times',
    'Ad-free experience',
  ],
} as const;

/** Row-by-row Free vs Premium comparison for the /pricing page table (AI-7823). */
export interface PlanComparisonRow {
  feature: string;
  /** `true` = included, `false` = not included, string = a specific limit */
  free: boolean | string;
  premium: boolean | string;
}

export const PLAN_COMPARISON_ROWS: readonly PlanComparisonRow[] = [
  {
    feature: 'AI companion conversations',
    free: `${FREE_TIER_LIMITS.dailyChatConversations} per day`,
    premium: 'Unlimited',
  },
  {
    feature: 'Conversation modes',
    free: `${FREE_TIER_LIMITS.chatModes.length} (Study)`,
    premium: `All ${ALL_CHAT_MODES.length}`,
  },
  {
    feature: 'Journal types',
    free: 'Freeform',
    premium: `All ${ALL_JOURNAL_TYPES.length} (SOAP, Prayer, Gratitude, Freeform)`,
  },
  {
    feature: 'Saved verses',
    free: `Up to ${FREE_TIER_LIMITS.maxSavedVerses}`,
    premium: 'Unlimited collections',
  },
  { feature: 'Full Bible reader (offline)', free: true, premium: true },
  { feature: 'Daily check-in', free: true, premium: true },
  { feature: 'Growth dashboard with full analytics', free: false, premium: true },
  { feature: 'Priority AI response times', free: false, premium: true },
  { feature: 'Ad-free experience', free: false, premium: true },
] as const;

/**
 * Check if a plan has premium access
 */
export function isPremium(plan: SubscriptionPlan, status: SubscriptionStatus): boolean {
  return plan === 'premium' && (status === 'active' || status === 'past_due');
}
